import { useEffect, useMemo, useState } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { useGAEvents } from '@utils/callGAEvents'
import { ClientApiClient } from '@utils/clientApi'

import { AccountType } from '@model/AccountType'
import { TestAnswerOutcomeStatus, TestAnswersDto } from '@model/ClientTestAnswerDto'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentCategoryOptionType } from '@model/DocumentCategoryOptionType'
import { TestFormValues } from '@model/Test'
import { TickmillProductType } from '@model/TickmillProductType'

import { useClientTestSubmission } from '@hooks/useClientTestSubmission'
import { scrollToTop } from '@hooks/useScrollToTop'

import { canTakeTest } from '../appropriatenessTestStatus'
import { useAccountAppTestFetch } from './useAccountAppTestFetch'

const useProductAppTestMutate = (productId: TickmillProductType) => {
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const navigate = useNavigate()
  const { account } = useAccountReadContext()
  const entity = useSessionEntity()
  const { callGAEvents } = useGAEvents()

  const { performSubmission, submitTest, uploadDocuments, isLoading } = useClientTestSubmission()

  const [isSuccess, setSuccess] = useState(false)
  const [isFailed, setIsFailed] = useState(false)
  const [accountApTestAnswers, setAccountApTestAnswers] = useState<TestAnswersDto | undefined>()

  const mutate = async (values: TestFormValues) => {
    await performSubmission(async () => {
      await submitTest(values, {
        clientId: values.clientId,
        category: values.category,
        testId: values.testId,
      })

      const answers = await apiClient.getAccountAppTestAnswers(locale, productId)
      setAccountApTestAnswers(answers)

      if (account?.type.id !== AccountType.Corporate) {
        callGAEvents({
          entity,
          kycVerificationProvider: account?.visibilityConfiguration?.kycVerificationProvider,
          onboardingFlowType: account?.visibilityConfiguration?.onboardingFlowType?.toString(),
          products: account?.tickmillProducts?.toString(),
        }).Onboarding.AppTest.submit()
      }

      if (answers.isClientVulnerable) {
        return navigate(`/dashboard/product-registration/${productId}/appropriateness-test/denied`)
      }

      await uploadDocuments(values, {
        categoryId: DocumentCategoryType.AppropriatenessTest,
        typeId: DocumentCategoryOptionType.TradingStatement,
        ticketIdOrTestId: answers.id,
      })

      if (answers.outcome?.outcome === TestAnswerOutcomeStatus.FAILED) {
        setIsFailed(true)
      } else {
        setSuccess(true)
      }
      scrollToTop()
    })
  }

  return { mutate, isLoading, isSuccess, isFailed, accountApTestAnswers }
}

export const useProductAppropriatenessTest = (productId: TickmillProductType) => {
  const { account } = useAccountReadContext()
  const [formValues, setFormValues] = useState<TestFormValues>()
  const [step, setStep] = useState('')
  const [registerProductStep, setRegisterProductStep] = useState(0)

  const { accountAppTest, accountAppTestAnswers, isLoading } = useAccountAppTestFetch(productId)
  const mutation = useProductAppTestMutate(productId)

  const isAllTestsPresent = useMemo(() => {
    const isCFDTestsExists = !!accountAppTest?.sections?.find(
      (appTest) => appTest?.code === 'cfd_investment_knowledge'
    )

    const isETDTestsExists = !!accountAppTest?.sections?.find(
      (appTest) => appTest?.code === 'futures_investment_knowledge'
    )

    return isCFDTestsExists && isETDTestsExists
  }, [accountAppTest?.sections])

  const defaultTestSections = accountAppTest?.sections
  useEffect(() => {
    const [section] = defaultTestSections || []
    setStep(section?.code || '')
  }, [defaultTestSections])

  const handleSubmit = async (values: TestFormValues) => {
    await mutation.mutate(values)
  }

  const handleNextStep = (values: TestFormValues, callback?: () => void) => {
    const sections = defaultTestSections || []
    const stepIndex = sections.findIndex((x) => x.code === step)
    const section = sections?.[stepIndex + 1]

    const answers = {
      ...values,
      freeAnswers: {
        ...formValues?.freeAnswers,
        ...values.freeAnswers,
      },
      selectedAnswers: {
        ...formValues?.selectedAnswers,
        ...values.selectedAnswers,
      },
    }

    if (section?.code) {
      setRegisterProductStep(stepIndex + 1)
      setStep(section.code)
      setFormValues(answers)
      scrollToTop()
    }

    callback?.()
  }

  const handlePrevStep = (values: TestFormValues, callback?: () => void) => {
    const sections = defaultTestSections || []
    const stepIndex = sections.findIndex((x) => x.code === step)
    const section = sections?.[stepIndex - 1]

    const answers = {
      ...values,
      freeAnswers: {
        ...formValues?.freeAnswers,
        ...values.freeAnswers,
      },
      selectedAnswers: {
        ...formValues?.selectedAnswers,
        ...values.selectedAnswers,
      },
    }
    setFormValues(answers)

    if (section?.code) {
      setRegisterProductStep(stepIndex - 1)
      setStep(section.code)
      scrollToTop()
    }

    callback?.()
  }

  return {
    formValues,
    handlePrevStep,
    handleNextStep,
    handleSubmit,
    mutation,
    accountAppTest,
    accountAppTestAnswers,
    defaultTestSections,
    isLoading:
      isLoading ||
      !!(
        !canTakeTest(account, accountAppTestAnswers, productId) &&
        account &&
        accountAppTestAnswers
      ),
    hasAllTests: isAllTestsPresent,
    step,
    registerProductStep,
    account,
  }
}
